(() => {
  if (customElements.get('fe-discount-countdown')) return;
  const instances = new Set();
  const headerStyles = new Map();
  let frame = 0;

  function parseDate(value) {
    if (!value) return null;
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(Z|[+-]\d{2}:\d{2})$/);
    if (!match) return NaN;
    const [, year, month, day, hour, minute, second = '0', zone] = match;
    const days = new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate();
    if (+month < 1 || +month > 12 || +day < 1 || +day > days || +hour > 23 || +minute > 59 || +second > 59) return NaN;
    if (zone !== 'Z' && (+zone.slice(1, 3) > 14 || +zone.slice(4) > 59 || (+zone.slice(1, 3) === 14 && +zone.slice(4) !== 0))) return NaN;
    return Date.parse(value.trim());
  }

  function queueLayout() {
    if (!frame) frame = requestAnimationFrame(layout);
  }

  function restoreHeaders() {
    headerStyles.forEach((original, element) => {
      if (original.value) element.style.setProperty('top', original.value, original.priority);
      else element.style.removeProperty('top');
    });
    headerStyles.clear();
  }

  function layout() {
    frame = 0;
    restoreHeaders();
    const navigation = document.querySelector('[data-fe-custom-navigation]')?.closest('.shopify-section');
    const announcements = [...document.querySelectorAll('fe-announcement')].map(element => element.closest('.shopify-section')).filter(Boolean);
    const headers = [...new Set([...announcements, navigation].filter(Boolean))];
    const visible = [];
    for (const element of instances) {
      const style = getComputedStyle(element);
      const position = style.getPropertyValue('--fe-position').trim() || 'section';
      const sticky = style.getPropertyValue('--fe-sticky').trim() === '1';
      const show = style.getPropertyValue('--fe-visible').trim() !== '0';
      const offset = Math.max(0, parseFloat(style.getPropertyValue('--fe-offset')) || 0);
      const host = element.host;
      element.reposition(position);
      host.style.removeProperty('height');
      host.style.removeProperty('top');
      host.style.removeProperty('position');
      host.style.removeProperty('z-index');
      element.style.removeProperty('position');
      element.style.removeProperty('inset');
      element.style.removeProperty('top');
      element.style.removeProperty('bottom');
      element.style.removeProperty('z-index');
      host.style.display = element.hidden || !show ? 'none' : '';
      if (element.hidden || !show) continue;
      const height = element.getBoundingClientRect().height;
      visible.push({element, host, position, sticky, offset, height});
    }
    let topSpace = 0;
    let bottomSpace = 0;
    const preceding = (host, header) => header && !!(host.compareDocumentPosition(header) & Node.DOCUMENT_POSITION_FOLLOWING);
    for (const item of visible) {
      const {element, host, position, sticky, offset, height} = item;
      if (!sticky) continue;
      if (position === 'top' || position === 'bottom') {
        // Raise the section too: its grid-item stacking context contains the fixed bar.
        host.style.position = 'relative';
        host.style.zIndex = '29';
      }
      const beforeHeader = position === 'section' && preceding(host, headers[0]);
      if (position === 'top' || beforeHeader) {
        if (position === 'top') {
          element.style.position = 'fixed';
          element.style.inset = 'auto 0 auto 0';
          element.style.top = `${topSpace + offset}px`;
          element.style.zIndex = '29';
          host.style.height = `${height + offset}px`;
        } else {
          host.style.position = 'sticky';
          host.style.top = `${topSpace + offset}px`;
          host.style.zIndex = '29';
        }
        topSpace += height + offset;
      } else if (position === 'bottom') {
        element.style.position = 'fixed';
        element.style.inset = 'auto 0 auto 0';
        element.style.bottom = `${bottomSpace + offset}px`;
        element.style.zIndex = '29';
        host.style.height = `${height + offset}px`;
        bottomSpace += height + offset;
      }
    }
    let headerBottom = topSpace;
    for (const header of headers) {
      const style = getComputedStyle(header);
      if (!['sticky', 'fixed'].includes(style.position) || style.display === 'none') continue;
      const top = parseFloat(style.top) || 0;
      if (topSpace) {
        headerStyles.set(header, {value: header.style.getPropertyValue('top'), priority: header.style.getPropertyPriority('top')});
        header.style.setProperty('top', `${top + topSpace}px`, 'important');
      }
      headerBottom = Math.max(headerBottom, top + topSpace + header.getBoundingClientRect().height);
    }
    for (const {host, position, sticky, offset, height} of visible) {
      if (position !== 'section' || !sticky || preceding(host, headers[0])) continue;
      host.style.position = 'sticky';
      host.style.top = `${headerBottom + offset}px`;
      host.style.zIndex = '29';
      headerBottom += height + offset;
    }
  }

  class FloorEasyCountdown extends HTMLElement {
    connectedCallback() {
      if (this.moving || this.ready) return;
      this.ready = true;
      this.host = this.closest('.shopify-section') || this;
      this.anchor = document.createComment('fe-countdown-position');
      this.host.before(this.anchor);
      this.editor = this.dataset.designMode === 'true';
      this.start = parseDate(this.dataset.start);
      this.end = parseDate(this.dataset.end);
      this.timerElement = this.querySelector('.fe-countdown__timer');
      this.message = this.querySelector('.fe-countdown__message');
      this.expired = this.querySelector('.fe-countdown__expired');
      this.notice = this.querySelector('.fe-countdown__notice');
      this.link = this.querySelector('.fe-countdown__link');
      this.closeButton = this.querySelector('[data-countdown-close]');
      this.storageKey = `fe-countdown-dismissed:${this.id}:${this.dataset.end}`;
      this.dismissed = false;
      try { this.dismissed = !this.editor && sessionStorage.getItem(this.storageKey) === '1'; } catch (_) {}
      this.onClose = () => {
        if (this.editor) return;
        this.dismissed = true;
        try { sessionStorage.setItem(this.storageKey, '1'); } catch (_) {}
        this.tick();
      };
      this.closeButton?.addEventListener('click', this.onClose);
      this.onVisible = () => this.tick();
      document.addEventListener('visibilitychange', this.onVisible);
      this.observer = new ResizeObserver(queueLayout);
      this.observer.observe(this);
      document.querySelectorAll('fe-announcement,[data-fe-custom-navigation]').forEach(header => this.observer.observe(header));
      instances.add(this);
      this.tick();
    }

    reposition(position) {
      if (this.currentPosition === position) return;
      this.moving = true;
      if (position === 'top') {
        const first = document.querySelector('.shopify-section-group-header-group') || document.querySelector('main');
        if (first && first !== this.host) first.before(this.host);
        else if (!first) document.body.prepend(this.host);
      } else if (position === 'bottom') {
        const footer = [...document.querySelectorAll('.shopify-section-group-footer-group')].pop();
        if (footer && footer !== this.host) footer.after(this.host);
        else document.body.append(this.host);
      } else if (this.anchor.isConnected) {
        this.anchor.after(this.host);
      }
      this.moving = false;
      this.currentPosition = position;
    }

    tick() {
      clearTimeout(this.timeout);
      const now = Date.now();
      const invalid = !Number.isFinite(this.end) || (this.start !== null && (!Number.isFinite(this.start) || this.start >= this.end));
      let state = this.dataset.enabled === 'false' ? 'disabled' : invalid ? 'invalid' : now < this.start ? 'scheduled' : now >= this.end ? 'expired' : 'running';
      const preview = this.editor && state !== 'running';
      const expiry = this.dataset.expiry || 'hide';
      this.hidden = this.dismissed || (!this.editor && (state === 'disabled' || state === 'invalid' || state === 'scheduled' || (state === 'expired' && expiry === 'hide')));
      this.dataset.state = state;
      if (this.notice) {
        this.notice.hidden = !preview;
        this.notice.textContent = preview ? (this.dataset[`notice${state[0].toUpperCase()}${state.slice(1)}`] || '') : '';
      }
      const expiredMessage = state === 'expired' && expiry === 'message';
      if (this.expired) this.expired.hidden = !expiredMessage;
      if (this.message) this.message.hidden = expiredMessage;
      if (this.timerElement) this.timerElement.hidden = expiredMessage;
      if (this.link) this.link.hidden = state === 'expired';
      if (!this.hidden) {
        let total = preview && state !== 'expired' ? (4 * 86400 + 8 * 3600 + 27 * 60 + 30) : Math.max(0, Math.ceil((this.end - now) / 1000));
        const days = Math.floor(total / 86400);
        const hours = this.dataset.showDays === 'false' ? Math.floor(total / 3600) : Math.floor(total % 86400 / 3600);
        const values = {days, hours, minutes: Math.floor(total % 3600 / 60), seconds: total % 60};
        this.querySelectorAll('[data-unit]').forEach(node => {
          const value = String(values[node.dataset.unit]).padStart(node.dataset.unit === 'days' ? 1 : 2, '0');
          if (node.textContent !== value) node.textContent = value;
        });
      }
      queueLayout();
      if (!this.dismissed && !invalid && this.dataset.enabled !== 'false' && state !== 'expired') {
        const delay = state === 'scheduled' ? Math.min(60000, Math.max(1, this.start - now)) : document.hidden ? 10000 : 1000;
        this.timeout = setTimeout(() => this.tick(), delay);
      }
    }

    disconnectedCallback() {
      if (this.moving) return;
      clearTimeout(this.timeout);
      this.observer?.disconnect();
      this.closeButton?.removeEventListener('click', this.onClose);
      document.removeEventListener('visibilitychange', this.onVisible);
      this.anchor?.remove();
      instances.delete(this);
      this.ready = false;
      queueLayout();
    }
  }

  customElements.define('fe-discount-countdown', FloorEasyCountdown);
  window.addEventListener('resize', queueLayout, {passive: true});
  document.addEventListener('shopify:section:reorder', queueLayout);
  document.addEventListener('shopify:section:load', queueLayout);
  document.addEventListener('shopify:section:unload', queueLayout);
})();

